import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

const INITIAL = {
  product: '',
  area: '',
  notes: '',
  name: '',
  phone: '',
  email: '',
  city: '',
};

const TOTAL_STEPS = 3;

export const QuoteModal = ({ isOpen, onClose, data, language = 'bg' }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      firstFieldRef.current?.focus?.();
    }, 320);
    return () => clearTimeout(t);
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setStep(1);
        setForm(INITIAL);
        setStatus(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!data) return null;

  const update = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const canAdvance = () => {
    if (step === 1) return !!form.product;
    if (step === 2) return form.area.trim().length > 0;
    if (step === 3) return form.name.trim() && (form.phone.trim() || form.email.trim());
    return false;
  };

  const handleNext = () => {
    if (!canAdvance()) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAdvance()) return;
    setSubmitting(true);
    setStatus(null);
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'quote',
          language,
          ...form,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Server error');
      }
      setStatus('success');
    } catch (err) {
      console.error('Quote form error:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-stretch sm:items-center justify-center transition-opacity duration-300',
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )}
      aria-hidden={!isOpen}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={data.close}
        tabIndex={isOpen ? 0 : -1}
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-title"
        className={cn(
          'relative w-full sm:max-w-2xl bg-paper text-ink shadow-2xl transition-all duration-300 ease-out-quart',
          'sm:rounded-xs flex flex-col max-h-[100dvh] sm:max-h-[90vh] overflow-hidden',
          isOpen ? 'translate-y-0 sm:scale-100' : 'translate-y-4 sm:scale-[0.98]'
        )}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-6 sm:px-10 sm:pt-10">
          <div className="min-w-0">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted">
              {data.stepLabel} {step} {data.ofLabel} {TOTAL_STEPS}
            </span>
            <h2
              id="quote-title"
              className="mt-3 font-serif tracking-editorial leading-[1.05] text-balance"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
            >
              {data.title}
            </h2>
            {data.subtitle && (
              <p className="mt-2 text-sm sm:text-base text-ink-muted">{data.subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={data.close}
            className="shrink-0 -mr-2 -mt-2 w-10 h-10 inline-flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
              <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="mt-6 px-6 sm:px-10">
          <div className="flex gap-1" role="progressbar" aria-valuemin={1} aria-valuemax={TOTAL_STEPS} aria-valuenow={step}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-[2px] flex-1 transition-colors duration-500',
                  i + 1 <= step ? 'bg-ink' : 'bg-rule'
                )}
              />
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 sm:px-10 py-8 sm:py-10"
          noValidate
        >
          {status === 'success' ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 gap-6">
              <svg viewBox="0 0 48 48" className="w-14 h-14 text-brass-deep" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" />
                <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M15 24l7 7 12-14" />
              </svg>
              <p className="font-serif text-xl sm:text-2xl tracking-editorial leading-snug text-balance max-w-sm">
                {data.success}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 group inline-flex items-center gap-3 text-ink"
              >
                <span className="relative font-sans text-sm tracking-[0.18em] uppercase">
                  {data.close}
                  <span className="absolute left-0 -bottom-1 w-full h-px bg-ink transition-transform duration-500 ease-out-quart origin-right scale-x-100 group-hover:origin-left" />
                </span>
              </button>
            </div>
          ) : (
            <>
              {step === 1 && (
                <div role="radiogroup" aria-label={data.productLabel}>
                  <span className="block font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted mb-5">
                    {data.productLabel}
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(data.productOptions || []).map((option, i) => {
                      const selected = form.product === option;
                      return (
                        <button
                          key={option}
                          ref={i === 0 ? firstFieldRef : null}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setForm((p) => ({ ...p, product: option }))}
                          className={cn(
                            'relative flex items-center gap-4 px-5 py-4 text-left cursor-pointer border border-rule transition-colors duration-200',
                            selected
                              ? 'bg-ink text-paper border-ink'
                              : 'hover:bg-paper-soft'
                          )}
                        >
                          <span
                            className={cn(
                              'font-mono text-[0.7rem] tabular-nums tracking-widest',
                              selected ? 'text-paper/70' : 'text-ink-faint'
                            )}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="font-serif tracking-editorial text-base sm:text-lg leading-tight">
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <QuoteField
                    id="area"
                    label={data.areaLabel}
                    value={form.area}
                    onChange={update('area')}
                    placeholder={data.areaPlaceholder}
                    inputRef={firstFieldRef}
                    required
                  />
                  <QuoteField
                    id="notes"
                    as="textarea"
                    rows={5}
                    label={data.notesLabel}
                    value={form.notes}
                    onChange={update('notes')}
                    placeholder={data.notesPlaceholder}
                  />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <QuoteField
                    id="name"
                    label={data.nameLabel}
                    value={form.name}
                    onChange={update('name')}
                    autoComplete="name"
                    inputRef={firstFieldRef}
                    required
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <QuoteField
                      id="phone"
                      type="tel"
                      label={data.phoneLabel}
                      value={form.phone}
                      onChange={update('phone')}
                      autoComplete="tel"
                    />
                    <QuoteField
                      id="email"
                      type="email"
                      label={data.emailLabel}
                      value={form.email}
                      onChange={update('email')}
                      autoComplete="email"
                    />
                  </div>
                  <QuoteField
                    id="city"
                    label={data.cityLabel}
                    value={form.city}
                    onChange={update('city')}
                    autoComplete="address-level2"
                  />
                </div>
              )}
            </>
          )}
        </form>

        {status !== 'success' && (
          <footer className="border-t border-rule px-6 sm:px-10 py-5 flex items-center justify-between gap-4 bg-paper-soft">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 1}
              className={cn(
                'group inline-flex items-center gap-3 text-ink transition-opacity',
                step === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
              )}
            >
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-px overflow-visible text-ink transition-transform duration-500 ease-out-quart group-hover:-translate-x-1" aria-hidden="true">
                <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M24 12H0m0 0l6-6m-6 6l6 6" />
              </svg>
              <span className="font-sans text-sm tracking-[0.18em] uppercase">{data.back}</span>
            </button>

            <div className="flex items-center gap-6">
              {status === 'error' && (
                <p className="text-sm text-clay" role="alert">{data.error}</p>
              )}
              {step < TOTAL_STEPS ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canAdvance()}
                  className="group inline-flex items-center gap-3 text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="font-sans text-sm tracking-[0.18em] uppercase">{data.next}</span>
                  <svg viewBox="0 0 24 24" fill="none" className="w-5 h-px overflow-visible text-ink transition-transform duration-500 ease-out-quart group-hover:translate-x-1 group-disabled:translate-x-0" aria-hidden="true">
                    <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={!canAdvance() || submitting}
                  className="group inline-flex items-center gap-3 text-paper bg-ink px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brass-deep transition-colors"
                >
                  <span className="font-sans text-sm tracking-[0.18em] uppercase">
                    {submitting ? data.sending : data.submit}
                  </span>
                  <svg viewBox="0 0 24 24" fill="none" className="w-5 h-px overflow-visible text-paper transition-transform duration-500 ease-out-quart group-hover:translate-x-1" aria-hidden="true">
                    <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
                  </svg>
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

const QuoteField = ({
  id,
  label,
  as = 'input',
  type = 'text',
  rows,
  value,
  onChange,
  required,
  autoComplete,
  placeholder,
  inputRef,
}) => {
  const common = {
    id,
    name: id,
    value,
    onChange,
    required,
    autoComplete,
    placeholder: placeholder || label,
    ref: inputRef,
    className: cn(
      'peer w-full bg-transparent border-b border-rule text-ink py-3 pr-1',
      'placeholder-transparent focus:outline-none focus:border-ink',
      'transition-colors duration-300 text-base'
    ),
  };
  return (
    <div className="relative">
      {as === 'textarea' ? (
        <textarea {...common} rows={rows} />
      ) : (
        <input {...common} type={type} />
      )}
      <label
        htmlFor={id}
        className={cn(
          'absolute left-0 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted',
          'transition-all duration-300 pointer-events-none',
          'top-0 peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-ink-faint',
          'peer-focus:top-0 peer-focus:text-[0.68rem] peer-focus:uppercase peer-focus:tracking-[0.22em] peer-focus:text-ink'
        )}
      >
        {label}
        {required && <span className="ml-1" aria-hidden="true">*</span>}
      </label>
    </div>
  );
};
